import { supabase } from '@/integrations/supabase/client';
import type { CommunityPost, CommunityReply, PostDocument } from '@/types/community';

type CerebroMedia = {
  id: string;
  object_path: string;
  original_name: string;
  mime_type: string;
  byte_size: number;
  signed_url?: string;
};

type CerebroPost = {
  id: string;
  area_id: string;
  area: { slug: string; name: string };
  author_user_id: string;
  author: { display_name: string | null; avatar_url: string | null };
  title: string | null;
  content: string;
  is_pinned: boolean;
  allow_comments: boolean;
  comments_count: number;
  reactions_count: number;
  user_reactions?: string[];
  media?: CerebroMedia[];
  created_at: string;
  updated_at: string;
  edited_at?: string | null;
};

type CerebroComment = {
  id: string;
  post_id: string;
  parent_comment_id: string | null;
  author_user_id: string;
  author: { display_name: string | null; avatar_url: string | null };
  content: string;
  reactions_count: number;
  user_reactions?: string[];
  media?: CerebroMedia[];
  created_at: string;
  updated_at: string;
};

export const COMMUNITY_MEDIA_BUCKET = 'forjahub-community-media';

export async function rpc<T = unknown>(name: string, params: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await (supabase as any).rpc(name, params);
  if (error) throw error;
  return data as T;
}

export async function listAreas() {
  return rpc<Array<{ id: string; slug: string; name: string; description: string | null; icon: string | null; position: number; is_locked: boolean; can_post: boolean }>>('list_community_areas');
}

async function signMedia(media: CerebroMedia[] = []): Promise<CerebroMedia[]> {
  if (!media.length) return [];
  const { data, error } = await supabase.storage
    .from(COMMUNITY_MEDIA_BUCKET)
    .createSignedUrls(media.map(item => item.object_path), 15 * 60);
  if (error) throw error;
  return media.map((item, index) => ({ ...item, signed_url: data?.[index]?.signedUrl }));
}

export async function toOriginalPost(value: CerebroPost): Promise<CommunityPost> {
  const media = await signMedia(value.media || []);
  const image = media.find(item => item.mime_type.startsWith('image/'));
  const documents: PostDocument[] = media
    .filter(item => !item.mime_type.startsWith('image/') && !item.mime_type.startsWith('video/'))
    .map(item => ({ url: item.signed_url || '', name: item.original_name, size: item.byte_size }));
  return {
    id: value.id,
    post_type: value.area?.slug || 'geral',
    title: value.title,
    content: value.content,
    image_url: image?.signed_url || null,
    user_id: value.author_user_id,
    is_closed: !value.allow_comments,
    closed_at: null,
    closed_by: null,
    auto_close_at: null,
    is_pinned: value.is_pinned,
    is_hidden: false,
    reactions_count: value.reactions_count || 0,
    replies_count: value.comments_count || 0,
    allow_replies: value.allow_comments,
    created_at: value.created_at,
    link_url: null,
    video_url: null,
    updated_at: value.updated_at,
    documents,
    profiles: value.author,
    tags: [],
    user_reactions: (value.user_reactions || []).map(reaction_type => ({ reaction_type })),
  };
}

export async function toOriginalReply(value: CerebroComment): Promise<CommunityReply> {
  const media = await signMedia(value.media || []);
  const image = media.find(item => item.mime_type.startsWith('image/'));
  return {
    id: value.id,
    post_id: value.post_id,
    user_id: value.author_user_id,
    parent_id: value.parent_comment_id,
    content: value.content,
    image_url: image?.signed_url || null,
    reactions_count: value.reactions_count || 0,
    is_hidden: false,
    created_at: value.created_at,
    updated_at: value.updated_at,
    profiles: value.author,
    user_reactions: (value.user_reactions || []).map(reaction_type => ({ reaction_type })),
    replies: [],
  };
}

function safeFileName(name: string) {
  return name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '-').replace(/-+/g, '-').slice(-100) || 'arquivo';
}

export async function attachFile(input: { postId?: string; commentId?: string; file: File }) {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData.user?.id;
  if (!userId) throw new Error('Sessão expirada.');
  if (input.file.size <= 0 || input.file.size > 25 * 1024 * 1024) throw new Error('Arquivo acima de 25 MB.');
  const target = input.postId || input.commentId;
  if (!target) throw new Error('Destino de mídia inválido.');
  const path = `${userId}/${target}/${crypto.randomUUID()}-${safeFileName(input.file.name)}`;
  const { error: uploadError } = await supabase.storage
    .from(COMMUNITY_MEDIA_BUCKET)
    .upload(path, input.file, { contentType: input.file.type, upsert: false });
  if (uploadError) throw uploadError;
  try {
    await rpc('register_community_media', {
      p_post_id: input.postId || null,
      p_comment_id: input.commentId || null,
      p_object_path: path,
      p_original_name: input.file.name,
      p_mime_type: input.file.type,
      p_byte_size: input.file.size,
    });
  } catch (error) {
    await supabase.storage.from(COMMUNITY_MEDIA_BUCKET).remove([path]);
    throw error;
  }
}

export type { CerebroPost, CerebroComment };
